import type { z } from 'zod';

import type { ShippingMapProps as ShippingMapSchema } from '../catalog';
import { useDataUrl } from '../useDataUrl';

type ShippingMapProps = z.infer<typeof ShippingMapSchema>;

export interface FlowItem {
  origin: string;
  destination: string;
  value: number;
  label?: string;
  group?: string;
  // Known port/terminal coordinates, when available - used in place of the
  // country centroid for a more accurate arc endpoint (see FlowLayer). Both
  // lat and lon must be present to take effect; a partial pair falls back to
  // the country centroid exactly as before.
  originLat?: number;
  originLon?: number;
  destinationLat?: number;
  destinationLon?: number;
}

export interface VesselItem {
  lat: number;
  lon: number;
  name?: string;
  imo?: string;
  speed?: number;
  heading?: number;
  destination?: string;
  status?: string;
}

export interface RouteItem {
  imo?: string;
  coordinates: [number, number][];
  color?: string;
  label?: string;
}

export interface MarkerItem {
  lat: number;
  lon: number;
  name?: string;
  color?: string;
  size?: number;
  group?: string;
  label?: string;
  tooltip?: string;
}

export interface ChoroplethItem {
  country: string;
  value: number;
  label?: string;
}

export interface PieMarkerSlice {
  label: string;
  value: number;
  color?: string;
}

export interface PieMarkerItem {
  lat: number;
  lon: number;
  name?: string;
  slices: PieMarkerSlice[];
}

/**
 * Fetches data for each ShippingMap layer via useDataUrl.
 *
 * Each layer has its own inline/url pair because flows, vessels, and choropleth
 * come from different queries with different shapes. The useDataUrl hook handles
 * the chatbot-data:// placeholder pattern and NaN sanitization.
 */
export function useMapData (props: ShippingMapProps) {
  const flows = useDataUrl(props.flowsUrl, props.flows) as FlowItem[];
  const vessels = useDataUrl(props.vesselsUrl, props.vessels) as VesselItem[];
  const routes = useDataUrl(props.routesUrl, props.routes) as RouteItem[];
  const markers = useDataUrl(props.markersUrl, props.markers) as MarkerItem[];
  const pieMarkers = useDataUrl(props.pieMarkersUrl, props.pieMarkers) as PieMarkerItem[];
  const choropleth = useDataUrl(
    props.choroplethUrl,
    props.choropleth,
  ) as ChoroplethItem[];

  return { flows, vessels, routes, markers, pieMarkers, choropleth };
}
